
// src/hooks/useWorkouts.js

import { useInfiniteQuery } from "@tanstack/react-query";
import { fetchWorkoutsPaginated } from "../../api/workoutApi";

/**
 * React Query hook that fetches workouts with infinite scroll/pagination.
 *
 * Returns the standard useInfiniteQuery result object:
 *   { data, isLoading, isError, error, fetchNextPage, hasNextPage, isFetchingNextPage, ... }
 *
 * `data` will contain pages with workouts arrays and pagination info.
 */
export const useWorkoutsInfinite = (limit = 10) =>
  useInfiniteQuery({
    queryKey: ["workouts", "infinite", limit],
    queryFn: ({ pageParam = 1 }) => 
      fetchWorkoutsPaginated({ page: pageParam, limit }),
    getNextPageParam: (lastPage) => {
      if (lastPage.pagination.hasNextPage) {
        return lastPage.pagination.page + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
  });

// Keep original hook for backward compatibility
export const useWorkouts = () => {
  const { data, ...rest } = useInfiniteQuery({
    queryKey: ["workouts", "infinite", 50],
    queryFn: ({ pageParam = 1 }) => 
      fetchWorkoutsPaginated({ page: pageParam, limit: 50 }),
    getNextPageParam: (lastPage) => {
      if (lastPage.pagination.hasNextPage) {
        return lastPage.pagination.page + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
  });

  // Flatten all workouts from all pages for backward compatibility
  const allWorkouts = data?.pages.flatMap(page => page.workouts) || [];
  
  return {
    data: allWorkouts,
    isLoading: rest.isLoading,
    isError: rest.isError,
    error: rest.error,
    refetch: rest.refetch,
    ...rest,
  };
};

/**
 * Derived selector: groups a flat workouts array by categoryId._id.
 * Pass the `data` from useWorkouts() directly.
 *
 * @param {Array} workouts - raw workouts array from the API
 * @returns {Object} - { [categoryId]: workout[] }
 */
export const groupWorkoutsByCategory = (workouts = []) =>
  workouts.reduce((acc, workout) => {
    const id = workout?.categoryId?._id;
    if (id) {
      if (!acc[id]) acc[id] = [];
      acc[id].push(workout);
    }
    return acc;
  }, {});